import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  ITmpOnboardingLink,
  ITmpWardRule,
  ITmpWardRuleRequest,
  ITmpRetryResult,
} from "../../../@types/admin/AdminTmpIntegration";
import { tmpIntegrationService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import {
  TMP_WARD_RULES_QUERY_KEY,
  TMP_SESSIONS_QUERY_KEY,
} from "../../queries/admin/tmpIntegration";

/**
 * Runs the provisioner for one Transmural Platform order now (EN-5203).
 * Invalidates every sessions query (list pages and the detail) so the row's
 * provisioning status and attempt count refresh.
 */
export function useRetryTmpSession() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ITmpRetryResult,
    Error,
    string
  >({
    mutationFn: async (telemonitoringId) =>
      ensureOk(await tmpIntegrationService.retrySession(telemonitoringId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TMP_SESSIONS_QUERY_KEY] });
    },
  });

  return { retrySession: mutateAsync, isRetrying: isPending };
}

/** Mints a fresh one-time completion link for the hospital clinician. */
export function useIssueTmpOnboardingLink() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ITmpOnboardingLink,
    Error,
    string
  >({
    mutationFn: async (telemonitoringId) =>
      ensureOk(
        await tmpIntegrationService.issueOnboardingLink(telemonitoringId)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TMP_SESSIONS_QUERY_KEY] });
    },
  });

  return { issueOnboardingLink: mutateAsync, isIssuingLink: isPending };
}

export function useAddTmpWardRule() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ITmpWardRule,
    Error,
    ITmpWardRuleRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await tmpIntegrationService.createWardRule(request)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TMP_WARD_RULES_QUERY_KEY],
      });
    },
  });

  return { createWardRule: mutateAsync, isCreatingWardRule: isPending };
}

export function useUpdateTmpWardRule() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ITmpWardRule,
    Error,
    { id: string; request: ITmpWardRuleRequest }
  >({
    mutationFn: async ({ id, request }) =>
      ensureOk(await tmpIntegrationService.updateWardRule(id, request)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TMP_WARD_RULES_QUERY_KEY],
      });
    },
  });

  return { updateWardRule: mutateAsync, isUpdatingWardRule: isPending };
}

export function useDeleteTmpWardRule() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<void, Error, string>({
    mutationFn: async (id) =>
      ensureOk(await tmpIntegrationService.deleteWardRule(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TMP_WARD_RULES_QUERY_KEY],
      });
    },
  });

  return { deleteWardRule: mutateAsync, isDeletingWardRule: isPending };
}
